import React from "react";

import Badge from "../Badge/Badge";
import Hyperlink from "../Hyperlink/Hyperlink";
import ResourceStatus, { StatusValues } from "../ResourceStatus/ResourceStatus";
import { ReactComponent as IconExternal } from "../../assets/icons/ripple-external-icon--delwp.svg";
import { ReactComponent as IconFile } from "../../assets/icons/file-icon.svg";

import styles from "./Card.module.css";
import { CellContentProps } from "../SearchResultsTable/BodyCell";
import { componentNanoid } from "../../utils/componentNanoid";
import { RowData } from "../SearchResultsTable/BodyRow";
import FormattedDate from "../Date/Date";
import CombineMetadata from "../Date/CombineMetadata";
import { joinClassNames } from "../../utils/joinClassNames";
import {
    formatFileSubtext,
    isVideoFileType,
} from "../../utils/formatFileSubtext";
import { shouldOpenLinkInNewWindow } from "../../utils/shouldOpenLinkInNewWindow";

export type CardLinkProps = {
    children: React.ReactNode;
    className: string;
    href?: string;
};

export type CardProps = {
    parts: RowData;
    resultsClass?: string;
};

const FileSubtext = ({
    fileType,
    fileSize,
}: {
    fileType: string;
    fileSize?: string;
}) => {
    const displayText = formatFileSubtext(fileType, fileSize);
    if (!displayText) {
        return null;
    }

    if (isVideoFileType(fileType)) {
        return <div className={styles.subtext}>{displayText}</div>;
    }

    return (
        <div className={styles.subtextWithIcon}>
            <IconFile aria-hidden="true" />
            <span>{displayText}</span>
        </div>
    );
};

export const CardDate = ({ data }: { data: string }) => {
    const date = new Intl.DateTimeFormat("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(new Date(data));

    return <>{date}</>;
};

const CardContent = ({
    cellDisplay = "text",
    data,
    url,
    label,
    isExternalLink = "false",
    determinationdateOfDecision = "",
    fileSize = "",
    fileType = "",
    header = "",
    isDfp = "",
    resultsClass,
}: CellContentProps & { resultsClass?: string }) => {
    const cellData = data?.trim() ?? "";
    switch (cellDisplay) {
        case "badge":
            return (
                <>
                    {cellData ? (
                        <p>
                            <strong>{label}</strong>
                            <br />
                            <Badge label={cellData} />
                        </p>
                    ) : null}
                </>
            );
        case "status":
            return (
                <>
                    {cellData ? (
                        <div className={styles.status}>
                            <strong>{label}</strong>
                            <br />
                            <ResourceStatus status={cellData as StatusValues} />
                        </div>
                    ) : null}
                </>
            );
        case "textLink":
            return url ? (
                <>
                    <h2 className={styles.heading}>
                        <Hyperlink
                            href={url}
                            className={styles.headingLink}
                            target={
                                shouldOpenLinkInNewWindow(resultsClass, fileType)
                                    ? "_blank"
                                    : undefined
                            }
                        >
                            {cellData}
                            {isExternalLink && (
                                <IconExternal aria-hidden="true" />
                            )}
                        </Hyperlink>
                    </h2>
                    {/* is Dfp for ministerial permits collection only */}
                    {isDfp == "1" ? (
                        <p className={styles.dfp}>
                            <strong>DFP</strong>
                        </p>
                    ) : (
                        ""
                    )}
                </>
            ) : (
                <p className={styles.headingText}>{cellData}</p>
            );
        case "date":
            return (
                <>
                    {cellData ? (
                        <p>
                            <strong>{label}</strong>
                            <br />
                            <FormattedDate label={cellData} />
                        </p>
                    ) : null}
                </>
            );
        case "combineMetadata":
            return (
                <p>
                    {cellData ? (
                        <>
                            <strong>{label}</strong>
                            <br />
                            <CombineMetadata
                                label={cellData}
                                determinationDate={determinationdateOfDecision}
                            />
                        </>
                    ) : null}
                </p>
            );
        case "inlineText":
            return cellData ? (
                <div className={styles.description}>
                    <strong>{label}:</strong>{" "}
                    {cellData.trim().replace("\\n", "\n")}
                </div>
            ) : null;
        case "subtext":
            if (header === "fileType") {
                return <FileSubtext fileType={data} fileSize={fileSize} />;
            }

            return cellData ? (
                <div className={styles.subtext}>
                    {cellData.trim().replace("\\n", "\n")}
                </div>
            ) : null;
        default:
            return (
                <>
                    {cellData ? (
                        <div className={styles.description}>
                            <strong>{label}</strong>
                            <br />
                            {cellData.trim().replace("\\n", "\n")}
                        </div>
                    ) : null}
                </>
            );
    }
};

const Card = ({ parts, resultsClass }: CardProps) => (
    <li
        className={joinClassNames([
            styles.card,
            ...(resultsClass ? [styles[resultsClass]] : []),
        ])}
    >
        {parts?.length &&
            parts.map((part) => (
                <CardContent
                    {...(part as CellContentProps)}
                    resultsClass={resultsClass}
                    key={`card-${componentNanoid()}`}
                />
            ))}
    </li>
);

export default Card;
